import { Box, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { LedgerAccountSelectorF0Document } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  LedgerAccountOption,
  LedgerAccountPartialValue,
  LedgerAccountSelectorF0,
  formatAccountName,
} from 'modules/banking/components/LedgerAccountSelectorF0'
import { WizardData } from 'modules/banking/components/NewAccountModal/types'
import { WizardStepLayout } from 'modules/banking/components/NewAccountModal/wizardSteps/WizardStepLayout'

export const LedgerAccountLinkStepF0 = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<WizardData>) => {
  useOnNext(() => Promise.resolve())

  const legalEntityId = data.legalEntity?.id

  // Check if there are any ledger accounts of type 'bank' for this legal entity
  const { data: ledgerData, loading } = useQuery(LedgerAccountSelectorF0Document, {
    variables: {
      legalEntityIds: legalEntityId ? [legalEntityId] : undefined,
      filter: { type: { in: ['bank'] } },
      perPage: 1,
    },
    skip: !legalEntityId,
  })

  const hasLedgerAccounts =
    !loading && (ledgerData?.finance?.accountsConnection?.totalCount ?? 0) > 0

  const selectedAccount = useUnsafeMemo((): LedgerAccountPartialValue | null => {
    if (!data.ledgerAccountId) return null
    return {
      id: data.ledgerAccountId,
      name: data.ledgerAccountName?.split(' - ')[1] ?? null,
      number: data.ledgerAccountName?.split(' - ')[0] ?? null,
    }
  }, [data.ledgerAccountId, data.ledgerAccountName])

  const handleChange = useUnsafeCallback(
    (account: LedgerAccountOption | null) => {
      onDataChanged({
        ...data,
        ledgerAccountId: account?.id ?? null,
        ledgerAccountName: formatAccountName(account) ?? null,
      })
    },
    [data, onDataChanged]
  )

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.finish_wizard.step_2.title')}
      subtitle={i18n.t('banking.accounts.finish_wizard.step_2.subtitle')}
    >
      <Box style={{ width: '500px' }} flexNoShrink flexDirection="column" gap="s16">
        {!loading && !hasLedgerAccounts && (
          <F0Alert
            variant="info"
            title={i18n.t('banking.accounts.finish_wizard.step_2.no_accounts_title')}
            description={i18n.t(
              'banking.accounts.finish_wizard.step_2.no_accounts_description'
            )}
          />
        )}
        {hasLedgerAccounts && (
          <Box width="full">
            {!!legalEntityId && (
              <LedgerAccountSelectorF0
                legalEntityId={legalEntityId}
                value={selectedAccount}
                types={['bank']}
                onChange={handleChange}
              />
            )}
          </Box>
        )}
      </Box>
    </WizardStepLayout>
  )
}
